import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import { customerService } from '../services/customerService';
import { customerInvoiceService } from '../services/customerInvoiceService';
import { customerPaymentService } from '../services/customerPaymentService';
import { orderService } from '../services/orderService';
import CurrencyTextField, { formatCurrency } from './CurrencyTextField';
import { formatDateOnly, formatLocalTodayYmd } from '../utils/dateUtils';

const PAYMENT_METHODS = ['Check', 'ACH', 'Cash', 'Wire', 'Credit Card'];

const RecordPaymentDialog = ({
  open,
  onClose,
  onSuccess,
  title = 'Record Payment',
  initialCustomerId = '',
  initialWorkOrderId = '',
  initialWorkOrderLabel = '',
  initialInvoiceId = '',
  initialAmount = '',
  lockCustomer = false,
  lockWorkOrder = false
}) => {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [workOrders, setWorkOrders] = useState([]);
  const [workOrderId, setWorkOrderId] = useState('');
  const [paymentDate, setPaymentDate] = useState(formatLocalTodayYmd());
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Check');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [openInvoices, setOpenInvoices] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [allocationTouched, setAllocationTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmRecordOpen, setConfirmRecordOpen] = useState(false);

  useEffect(() => {
    if (!open) setConfirmRecordOpen(false);
  }, [open]);

  useEffect(() => {
    if (open) {
      setError('');
      setConfirmRecordOpen(false);
      setAmount(initialAmount !== '' && initialAmount != null ? String(initialAmount) : '');
      setReferenceNumber('');
      setNotes('');
      setAllocations({});
      setAllocationTouched({});
      setCustomerId(initialCustomerId ? String(initialCustomerId) : '');
      setWorkOrderId(initialWorkOrderId ? String(initialWorkOrderId) : '');
      setWorkOrders([]);
      setPaymentDate(formatLocalTodayYmd());
      setPaymentMethod('Check');
      loadCustomers();
    }
  }, [open, initialAmount, initialCustomerId, initialWorkOrderId]);

  useEffect(() => {
    if (open && customerId) {
      if (!lockWorkOrder && !initialWorkOrderId) {
        setWorkOrderId('');
      }
      loadWorkOrdersForCustomer();
      loadOpenInvoices();
    } else {
      setWorkOrders([]);
      if (!(open && lockWorkOrder && initialWorkOrderId)) {
        setWorkOrderId('');
      }
      setOpenInvoices([]);
      setAllocations({});
      setAllocationTouched({});
    }
  }, [open, customerId, lockWorkOrder, initialWorkOrderId]);

  useEffect(() => {
    if (!open) return;
    if (!initialInvoiceId) return;
    if (openInvoices.length === 0) return;

    const target = openInvoices.find((inv) => String(inv.id) === String(initialInvoiceId));
    if (!target) return;

    const typedAmount = parseFloat(amount);
    const defaultAmtRaw = Number.isFinite(typedAmount)
      ? typedAmount
      : (
        initialAmount !== '' && initialAmount != null
          ? parseFloat(initialAmount)
          : parseFloat(target.balance_due)
      );
    const defaultAmt = Number.isFinite(defaultAmtRaw) ? Math.max(0, defaultAmtRaw) : 0;
    const maxAlloc = Number.isFinite(parseFloat(target.balance_due)) ? parseFloat(target.balance_due) : defaultAmt;
    const applied = Math.min(defaultAmt, maxAlloc);

    setAllocations((prev) => {
      if (allocationTouched[target.id]) return prev;
      return { ...prev, [target.id]: applied };
    });
  }, [open, initialInvoiceId, openInvoices, initialAmount, amount, allocationTouched]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const res = await customerService.getAll({ limit: 500 });
      setCustomers(res?.data ?? []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkOrdersForCustomer = async () => {
    try {
      const res = await orderService.getAll({
        customerId: parseInt(customerId, 10),
        limit: 300,
        include_superseded: '0'
      });
      setWorkOrders(res?.data ?? []);
    } catch {
      setWorkOrders([]);
    }
  };

  const loadOpenInvoices = async () => {
    try {
      const res = await customerInvoiceService.getOpenByCustomerId(customerId);
      setOpenInvoices(res?.data ?? []);
      setAllocations({});
      setAllocationTouched({});
      setAmount(initialAmount !== '' && initialAmount != null ? String(initialAmount) : '');
    } catch (err) {
      setOpenInvoices([]);
      setError(err.response?.data?.message || 'Failed to load open invoices');
    }
  };

  const totalAllocated = () =>
    Object.values(allocations).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const buildAllocList = () =>
    Object.entries(allocations)
      .filter(([, v]) => v && parseFloat(v) > 0)
      .map(([invId, v]) => ({ invoice_id: parseInt(invId, 10), amount: parseFloat(v) }));

  const paymentRemainder = () => {
    const amt = parseFloat(amount) || 0;
    return Math.round((amt - totalAllocated()) * 100) / 100;
  };

  const setAllocation = (invoiceId, val) => {
    const num = parseFloat(val);
    setAllocationTouched((prev) => ({ ...prev, [invoiceId]: true }));
    setAllocations((prev) => ({
      ...prev,
      [invoiceId]: isNaN(num) ? '' : num
    }));
  };

  const validateBeforeConfirm = () => {
    const amt = parseFloat(amount);
    if (!customerId || !amt || amt <= 0) {
      setError('Customer and a positive amount are required.');
      return false;
    }
    const allocList = buildAllocList();
    const totalAlloc = allocList.reduce((s, a) => s + a.amount, 0);
    if (totalAlloc > amt + 0.01) {
      setError(`Total applied (${formatCurrency(totalAlloc)}) cannot exceed payment amount (${formatCurrency(amt)}).`);
      return false;
    }
    return true;
  };

  const handleSubmitAttempt = () => {
    setError('');
    if (!validateBeforeConfirm()) return;
    setConfirmRecordOpen(true);
  };

  const handleConfirmRecord = async () => {
    setError('');
    const amt = parseFloat(amount);
    if (!customerId || !amt || amt <= 0) {
      setConfirmRecordOpen(false);
      return;
    }
    const allocList = buildAllocList();
    const woId = workOrderId ? parseInt(workOrderId, 10) : null;

    try {
      setSaving(true);
      await customerPaymentService.create({
        customer_id: parseInt(customerId, 10),
        work_order_id: Number.isFinite(woId) ? woId : undefined,
        payment_date: paymentDate,
        amount: amt,
        payment_method: paymentMethod,
        reference_number: referenceNumber || undefined,
        notes: notes || undefined,
        allocations: allocList
      });
      setConfirmRecordOpen(false);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const customerName = (c) =>
    c.company_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || `#${c.id}`;
  const formatDate = (d) => formatDateOnly(d, 'MM/dd/yyyy') || '—';
  const invoiceLabel = (inv) =>
    (inv?.identifier != null && String(inv.identifier).trim() !== ''
      ? String(inv.identifier).trim()
      : (inv?.invoice_number != null && String(inv.invoice_number).trim() !== ''
          ? String(inv.invoice_number).trim()
          : (inv?.id != null ? `#${inv.id}` : '—')));

  const orderLabel = (o) =>
    (o?.identifier != null && String(o.identifier).trim()) ||
    (o?.work_order_number != null && String(o.work_order_number)) ||
    (o?.id != null ? `#${o.id}` : 'Order');

  const workOrderOptions = useMemo(() => {
    const list = Array.isArray(workOrders) ? [...workOrders] : [];
    if (!workOrderId) return list;
    const hasCurrent = list.some((o) => String(o?.id) === String(workOrderId));
    if (hasCurrent) return list;
    return [
      {
        id: workOrderId,
        identifier: initialWorkOrderLabel || `#${workOrderId}`
      },
      ...list
    ];
  }, [workOrders, workOrderId, initialWorkOrderLabel]);

  const invoiceGroups = useMemo(() => {
    const byOrder = new Map();
    for (const inv of openInvoices) {
      const key = inv.work_order_id != null ? String(inv.work_order_id) : 'none';
      if (!byOrder.has(key)) {
        byOrder.set(key, {
          work_order_id: inv.work_order_id,
          label:
            inv.work_order_identifier ||
            inv.work_order_number ||
            (inv.work_order_id != null ? `Order #${inv.work_order_id}` : 'No order'),
          invoices: []
        });
      }
      byOrder.get(key).invoices.push(inv);
    }
    return Array.from(byOrder.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label))
    );
  }, [openInvoices]);

  const remainder = paymentRemainder();
  const confirmAmountLabel = formatCurrency(parseFloat(amount) || 0, '$0.00');
  const confirmAllocLabel = formatCurrency(totalAllocated(), '$0.00');
  const confirmRemainderLabel = formatCurrency(Math.max(0, remainder), '$0.00');

  return (
    <>
      <Dialog
        open={confirmRecordOpen}
        onClose={() => !saving && setConfirmRecordOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Record payment</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Record {confirmAmountLabel} payment?
            </Typography>
            {totalAllocated() > 0 && (
              <Typography variant="body2" color="text.secondary">
                Applied to invoices: {confirmAllocLabel}
              </Typography>
            )}
            {remainder > 0.01 && (
              <Typography variant="body2" color="text.secondary">
                Unapplied (customer credit): {confirmRemainderLabel}
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRecordOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleConfirmRecord} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
          <FormControl fullWidth size="small" required>
            <InputLabel>Customer</InputLabel>
            <Select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              label="Customer"
              disabled={loading || lockCustomer}
            >
              <MenuItem value="">Select customer</MenuItem>
              {(customers || []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {customerName(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={!customerId || lockWorkOrder}>
            <InputLabel>Primary order (optional)</InputLabel>
            <Select
              value={workOrderId}
              onChange={(e) => setWorkOrderId(e.target.value)}
              label="Primary order (optional)"
            >
              <MenuItem value="">All orders / none</MenuItem>
              {workOrderOptions.map((o) => (
                <MenuItem key={o.id} value={String(o.id)}>
                  {orderLabel(o)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Payment Date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <CurrencyTextField
              fullWidth
              size="small"
              label="Amount"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              minimumFractionDigits={2}
              maximumFractionDigits={2}
              dollarAdornment
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Method</InputLabel>
              <Select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                label="Method"
              >
                {PAYMENT_METHODS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Reference (e.g. check #)"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
            />
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Notes"
            multiline
            rows={1}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {customerId && openInvoices.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Apply to open invoices (all orders)
              </Typography>
              {invoiceGroups.map((group) => (
                <Box key={group.work_order_id ?? 'none'} sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {group.label}
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'surfaceMuted.main' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Due</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Balance</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Apply</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.invoices.map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell>{invoiceLabel(inv)}</TableCell>
                          <TableCell>{formatDate(inv.due_date)}</TableCell>
                          <TableCell align="right">{formatCurrency(inv.balance_due, '0')}</TableCell>
                          <TableCell align="right">
                            <CurrencyTextField
                              size="small"
                              value={allocations[inv.id] ?? ''}
                              onChange={(e) => setAllocation(inv.id, e.target.value)}
                              minimumFractionDigits={2}
                              maximumFractionDigits={2}
                              dollarAdornment
                              inputProps={{ min: 0, max: inv.balance_due, step: '0.01' }}
                              sx={{ width: 132 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ))}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Total applied: {formatCurrency(totalAllocated())} — Payment amount: {formatCurrency(amount || 0)}
                {remainder > 0.01 && (
                  <> — Unapplied (adds to customer credit): {formatCurrency(remainder)}</>
                )}
              </Typography>
            </Box>
          )}

          {customerId && openInvoices.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No open invoices for this customer. The full amount will remain unapplied on this payment (customer credit).
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmitAttempt} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default RecordPaymentDialog;
